import {
    Box,
    Button,
    Container,
    CopyToClipboard,
    Flashbar,
    Header,
    SpaceBetween,
    Spinner,
    StatusIndicator
} from "@amzn/awsui-components-react";
import * as React from "react";
import { useAPI2 } from "../../hooks/api-hook";
import { AppContext } from "../../setup/context";
import { ErrorFlashbar } from "../common/ErrorFlashbar";
import { AITeammateAccessSection } from "./AITeammateAccessSection";
import { DeleteKnowledgeBaseModal } from "./DeleteKnowledgeBaseModal";
import { KnowledgeBaseSyncStatusTable } from "./KnowledgeBaseSyncStatusTable";
import { KBMcpSection } from "./KBMcpSection";

type PendingAction = "Create" | "Delete";

const POLL_INTERVAL_MS = 12000;
/** Polls to tolerate while an action is in flight before giving up (throttles, network blips). */
const MAX_CONSECUTIVE_POLL_FAILURES = 5;

/**
 * Whether the status returned by getKB means nothing is in flight any more for the action the
 * admin triggered. A provisioning run reads as NOT_PROVISIONED until its ids are persisted, so
 * NOT_PROVISIONED only counts as settled when nothing was triggered, or when a deletion was.
 */
const isSettled = (status: string, pendingAction: PendingAction) => {
    if (pendingAction === "Create") {
        return status !== "NOT_PROVISIONED" && status !== "CREATING";
    }
    if (pendingAction === "Delete") {
        return status === "NOT_PROVISIONED" || status === "NOT_FOUND";
    }
    return status !== "CREATING" && status !== "UPDATING" && status !== "DELETING";
};

export const KnowledgeBaseTab = (props: {
    instanceId: string;
}) => {
    const context = React.useContext(AppContext);
    const getKBRunner = useAPI2(context.getKnowledgeBaseAPI().getKB);
    const createKBRunner = useAPI2(context.getKnowledgeBaseAPI().createKB);
    const deleteKBRunner = useAPI2(context.getKnowledgeBaseAPI().deleteKB);
    const reSyncRunner = useAPI2(context.getKnowledgeBaseAPI().triggerReSync);
    const [pendingAction, setPendingAction] = React.useState<PendingAction>(null);
    const [polling, setPolling] = React.useState<boolean>(false);
    const [notice, setNotice] = React.useState<string>(null);
    const [showDeleteModal, setShowDeleteModal] = React.useState<boolean>(false);
    const consecutivePollFailures = React.useRef(0);

    React.useEffect(() => {
        getKBRunner.invoke(props.instanceId);
    }, [props.instanceId]);

    // Provisioning and deletion take minutes, so poll until the reported status settles. The next
    // poll is armed only once the previous one has resolved (status left "Running"), so a slow
    // request never overlaps the next tick. Cleared on unmount and as soon as polling stops.
    React.useEffect(() => {
        if (!polling || getKBRunner.status === "Running") return;
        const timer = setTimeout(() => getKBRunner.invoke(props.instanceId), POLL_INTERVAL_MS);
        return () => clearTimeout(timer);
    }, [polling, getKBRunner.status, props.instanceId]);

    // `output` is a new object on every poll, so this re-evaluates on each result.
    React.useEffect(() => {
        if (getKBRunner.status === "Error") {
            // A transient poll failure must not collapse the in-flight view while the server-side
            // task is still running, so keep polling until the failures look persistent.
            consecutivePollFailures.current++;
            if (!pendingAction || consecutivePollFailures.current >= MAX_CONSECUTIVE_POLL_FAILURES) {
                setPolling(false);
                setPendingAction(null);
            }
            return;
        }
        if (getKBRunner.status !== "Succeeded" || !getKBRunner.output) return;
        consecutivePollFailures.current = 0;
        if (isSettled(getKBRunner.output.status, pendingAction)) {
            setPolling(false);
            setPendingAction(null);
        } else {
            setPolling(true);
        }
    }, [getKBRunner.status, getKBRunner.output]);

    React.useEffect(() => {
        if (createKBRunner.status !== "Succeeded") return;
        setNotice("Knowledge Base provisioning started. This takes a few minutes - the status below refreshes automatically.");
        consecutivePollFailures.current = 0;
        setPendingAction("Create");
        setPolling(true);
    }, [createKBRunner.status]);

    React.useEffect(() => {
        if (reSyncRunner.status !== "Succeeded") return;
        // Nothing to poll: the run is idempotent and re-runnable, so the UI only confirms it started.
        setNotice("Re-sync started. Every page opted in to the Knowledge Base will be re-indexed over the next few minutes.");
    }, [reSyncRunner.status]);

    React.useEffect(() => {
        if (deleteKBRunner.status !== "Succeeded") return;
        setNotice("Knowledge Base deletion started. This takes a few minutes - the status below refreshes automatically.");
        consecutivePollFailures.current = 0;
        setPendingAction("Delete");
        setPolling(true);
        setShowDeleteModal(false);
    }, [deleteKBRunner.status]);

    const kb = getKBRunner.output;
    const status = kb ? kb.status : null;
    // Keeps the in-flight view up while the triggered action has not settled, including the first
    // poll after triggering it, when the recorded status has not caught up yet.
    const inFlight = !!pendingAction && (!status || !isSettled(status, pendingAction))
        ? pendingAction
        : null;

    const deleteButton = (
        <Button
            loading={deleteKBRunner.status === "Running"}
            onClick={() => setShowDeleteModal(true)}
        >
            Delete Knowledge Base
        </Button>
    );

    // A function, not a value: it dereferences `kb`, which is null until the first load lands.
    const getDetails = () => (
        <SpaceBetween size="xxs">
            <Box variant="awsui-key-label">Knowledge Base ARN</Box>
            {kb.knowledgeBaseArn ?
                <CopyToClipboard
                    variant="inline"
                    textToCopy={kb.knowledgeBaseArn}
                    copyButtonAriaLabel="Copy Knowledge Base ARN"
                    copySuccessText="Knowledge Base ARN copied"
                    copyErrorText="Knowledge Base ARN failed to copy"
                /> :
                <Box color="text-status-inactive">Not available</Box>
            }
            <Box color="text-status-inactive" fontSize="body-s">
                Knowledge Base ID: {kb.knowledgeBaseId} | Data source ID: {kb.dataSourceId}
            </Box>
        </SpaceBetween>
    );

    const inProgress = (verb: string) => (
        <StatusIndicator type="in-progress">
            {verb} Knowledge Base - this takes a few minutes
        </StatusIndicator>
    );

    const getStatusContent = () => {
        if (inFlight) {
            return inProgress(inFlight === "Create" ? "Creating" : "Deleting");
        }
        switch (status) {
            case "NOT_PROVISIONED":
                return <SpaceBetween size="m">
                    <div>
                        Create a Knowledge Base for this instance to make the pages that are opted in
                        available to AI Teammate. Provisioning takes a few minutes. Once it is active,
                        give the Knowledge Base ARN shown here to AI Teammate.
                    </div>
                    <Button
                        variant="primary"
                        loading={createKBRunner.status === "Running"}
                        onClick={() => createKBRunner.invoke(props.instanceId)}
                    >
                        Create Knowledge Base
                    </Button>
                </SpaceBetween>;
            case "CREATING":
                return inProgress("Creating");
            case "DELETING":
                return inProgress("Deleting");
            case "ACTIVE":
                return <SpaceBetween size="m">
                    <StatusIndicator type="success">Active</StatusIndicator>
                    {getDetails()}
                    <SpaceBetween size="xs" direction="horizontal">
                        <Button
                            loading={reSyncRunner.status === "Running"}
                            onClick={() => reSyncRunner.invoke(props.instanceId)}
                        >
                            Re-sync pages
                        </Button>
                        {deleteButton}
                    </SpaceBetween>
                </SpaceBetween>;
            case "FAILED":
                return <SpaceBetween size="m">
                    <StatusIndicator type="error">Failed</StatusIndicator>
                    <div>
                        This Knowledge Base did not finish provisioning and cannot be used. Delete it
                        and create it again.
                    </div>
                    {deleteButton}
                </SpaceBetween>;
            case "NOT_FOUND":
                return <SpaceBetween size="m">
                    <StatusIndicator type="warning">Not found</StatusIndicator>
                    <div>
                        This instance records a Knowledge Base that no longer exists in Amazon Bedrock.
                        Delete it and create it again.
                    </div>
                    {deleteButton}
                </SpaceBetween>;
            default:
                return <SpaceBetween size="m">
                    <StatusIndicator type="in-progress">{status}</StatusIndicator>
                    {getDetails()}
                    {deleteButton}
                </SpaceBetween>;
        }
    };

    return (
        <SpaceBetween size="m">
            {notice && <Flashbar items={[{
                type: "info",
                content: notice,
                dismissible: true,
                dismissLabel: "Dismiss message",
                onDismiss: () => setNotice(null)
            }]} />}
            {getKBRunner.status === "Error" && <ErrorFlashbar error={getKBRunner.err} />}
            {createKBRunner.status === "Error" && <ErrorFlashbar error={createKBRunner.err} />}
            {deleteKBRunner.status === "Error" && <ErrorFlashbar error={deleteKBRunner.err} />}
            {reSyncRunner.status === "Error" && <ErrorFlashbar error={reSyncRunner.err} />}
            <Container header={
                <Header variant="h3"
                    description="Amazon Bedrock Knowledge Base for this instance. Pages that are opted in to the Knowledge Base are indexed so AI Teammate can answer from them."
                >
                    Pathfinder KB
                </Header>}>
                {getKBRunner.status === "Running" && !kb &&
                    <Box textAlign="center" className="awsui-util-status-inactive">
                        <Spinner /> Loading Knowledge Base
                    </Box>
                }
                {!!kb && getStatusContent()}
            </Container>
            {!inFlight && status === "ACTIVE" &&
                <Container>
                    <KnowledgeBaseSyncStatusTable instanceId={props.instanceId} />
                </Container>
            }
            {!inFlight && status === "ACTIVE" && kb.knowledgeBaseId && kb.knowledgeBaseArn &&
                <AITeammateAccessSection
                    instanceId={props.instanceId}
                    knowledgeBaseId={kb.knowledgeBaseId}
                    knowledgeBaseArn={kb.knowledgeBaseArn}
                />
            }
            {!inFlight && status === "ACTIVE" &&
                <KBMcpSection instanceId={props.instanceId} />
            }
            <DeleteKnowledgeBaseModal
                visible={showDeleteModal}
                loading={deleteKBRunner.status === "Running"}
                onDismiss={() => setShowDeleteModal(false)}
                onDelete={() => deleteKBRunner.invoke(props.instanceId)}
            />
        </SpaceBetween>
    );
};
